import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FileText, Plus, Search, AlertTriangle, BookOpenCheck, GraduationCap } from "lucide-react";

const STATUS_BADGE = {
  Draft: "bg-slate-200 text-slate-800",
  "In review": "bg-sky-700 text-white",
  "Pending approval": "bg-amber-600 text-white",
  Effective: "bg-[#134e4a] text-white",
  Retired: "bg-slate-400 text-white",
};

const EMPTY = {
  title: "", doc_type: "SOP", department: "Laboratory-wide", description: "",
  keywords: "", review_interval_months: 12, requires_acknowledgement: true,
};

export default function Documents() {
  const { can, user } = useAuth();
  const [meta, setMeta] = useState(null);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filters, setFilters] = useState({ q: "", doc_type: "", department: "", status: "", overdue: false, unread: false });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...EMPTY });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = { ...filters };
      if (!params.overdue) delete params.overdue;
      if (!params.unread) delete params.unread;
      const [list, sum] = await Promise.all([
        api.get("/documents", { params }),
        api.get("/documents/summary"),
      ]);
      setRows(list.data);
      setSummary(sum.data);
    } catch (e) { toast.error(apiError(e)); }
  }, [filters]);

  useEffect(() => { api.get("/documents/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/documents", {
        ...form,
        review_interval_months: Number(form.review_interval_months) || 12,
        keywords: form.keywords.split(",").map((k) => k.trim()).filter(Boolean),
      });
      toast.success(`${data.control_number} created — upload Rev 1 next`);
      setOpen(false);
      setForm({ ...EMPTY });
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const canAuthor = ["admin", "lab_director", "supervisor", "pathologist", "pathologist_assistant", "tech"].includes(user?.role);

  const Stat = ({ label, value, tone = "", testid, onClick }) => (
    <button onClick={onClick} disabled={!onClick} data-testid={testid}
      className={`border border-slate-200 bg-white p-3 text-left transition-colors ${onClick ? "hover:border-[#134e4a]" : "cursor-default"}`}>
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`num mt-1 text-2xl font-bold ${tone || "text-slate-900"}`}>{value}</div>
    </button>
  );

  return (
    <div className="space-y-6" data-testid="documents-page">
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#134e4a]">PolicyTraX</p>
          <h1 className="mt-1 flex items-center gap-2 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            <FileText size={26} className="text-[#134e4a]" /> SOP &amp; Document Manager
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            Every document gets a permanent control number, a revision history and a signed approval trail.
          </p>
        </div>
        {canAuthor && (
          <Button onClick={() => setOpen(true)} data-testid="new-document-button"
                  className="ml-auto h-11 bg-[#134e4a] hover:bg-[#0f3f3c]">
            <Plus size={16} className="mr-2" /> New document
          </Button>
        )}
        <Link to="/documents/matrix" data-testid="training-matrix-link"
              className={`${canAuthor ? "" : "ml-auto "}inline-flex h-11 items-center gap-2 border border-slate-300 px-4 text-sm font-semibold text-slate-700 transition-colors hover:border-[#134e4a] hover:text-[#134e4a]`}>
          <GraduationCap size={16} /> Training matrix
        </Link>
      </div>

      {summary && (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-5" data-testid="documents-summary">
          <Stat label="Effective" value={summary.effective} testid="doc-stat-effective"
                onClick={() => setFilters((f) => ({ ...f, status: "Effective", overdue: false, unread: false }))} />
          <Stat label="In review" value={summary.in_review} testid="doc-stat-in-review"
                onClick={() => setFilters((f) => ({ ...f, status: "In review", overdue: false, unread: false }))} />
          <Stat label="Pending approval" value={summary.pending_approval} tone="text-amber-700" testid="doc-stat-pending"
                onClick={() => setFilters((f) => ({ ...f, status: "Pending approval", overdue: false, unread: false }))} />
          <Stat label="Review overdue" value={summary.review_overdue} tone="text-red-800" testid="doc-stat-overdue"
                onClick={() => setFilters((f) => ({ ...f, status: "", overdue: true, unread: false }))} />
          <Stat label="Unread by me" value={summary.my_unread} tone="text-sky-800" testid="doc-stat-unread"
                onClick={() => setFilters((f) => ({ ...f, status: "", overdue: false, unread: true }))} />
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3 border border-slate-200 bg-slate-50 p-3">
        <div className="min-w-[220px] flex-1">
          <Label className="text-xs uppercase tracking-wide text-slate-600">Search</Label>
          <div className="relative mt-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                   data-testid="doc-search-input" placeholder="Control number, title or keyword"
                   className="h-11 border-slate-300 pl-9" />
          </div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-600">Type</Label>
          <select value={filters.doc_type} onChange={(e) => setFilters((f) => ({ ...f, doc_type: e.target.value }))}
                  data-testid="doc-type-filter" className="mt-1 h-11 border border-slate-300 bg-white px-3 text-sm">
            <option value="">All types</option>
            {(meta?.doc_types || []).map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-600">Department</Label>
          <select value={filters.department} onChange={(e) => setFilters((f) => ({ ...f, department: e.target.value }))}
                  data-testid="doc-department-filter" className="mt-1 h-11 border border-slate-300 bg-white px-3 text-sm">
            <option value="">All departments</option>
            {(meta?.departments || []).map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wide text-slate-600">Status</Label>
          <select value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value, overdue: false, unread: false }))}
                  data-testid="doc-status-filter" className="mt-1 h-11 border border-slate-300 bg-white px-3 text-sm">
            <option value="">All statuses</option>
            {(meta?.statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <Button variant="outline" onClick={() => setFilters({ q: "", doc_type: "", department: "", status: "", overdue: false, unread: false })}
                data-testid="doc-clear-filters" className="h-11 border-slate-300">Clear</Button>
      </div>

      <div className="border border-slate-200">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-sm" data-testid="documents-table">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr className="border-b border-slate-200">
                <th className="px-3 py-2">Control #</th><th className="px-3 py-2">Title</th>
                <th className="px-3 py-2">Type</th><th className="px-3 py-2">Department</th>
                <th className="px-3 py-2 text-right">Rev</th><th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Effective</th><th className="px-3 py-2">Next review</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.id} className="border-b border-slate-100 row-hover hover:bg-slate-50" data-testid={`doc-row-${d.id}`}>
                  <td className="px-3 py-2">
                    <Link to={`/documents/${d.id}`} className="font-mono font-semibold text-[#134e4a] hover:underline"
                          data-testid={`doc-link-${d.control_number}`}>
                      {d.control_number}
                    </Link>
                  </td>
                  <td className="px-3 py-2 text-slate-800">{d.title}</td>
                  <td className="px-3 py-2 text-slate-600">{d.doc_type}</td>
                  <td className="px-3 py-2 text-slate-600">{d.department}</td>
                  <td className="num px-3 py-2 text-right text-slate-700">{d.current_version || "—"}</td>
                  <td className="px-3 py-2">
                    <span className={`px-1.5 py-0.5 text-[11px] font-semibold ${STATUS_BADGE[d.status] || "bg-slate-200"}`}>
                      {d.status}
                    </span>
                    {d.pending_status && (
                      <span className="ml-1 bg-amber-100 px-1.5 py-0.5 text-[11px] font-semibold text-amber-900"
                            data-testid={`doc-pending-${d.id}`}>
                        Rev {d.pending_version} {d.pending_status.toLowerCase()}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-600">{d.effective_date ? fmtDateTime(d.effective_date) : "—"}</td>
                  <td className="px-3 py-2 text-xs">
                    {d.next_review_date ? (
                      <span className={d.review_overdue ? "font-semibold text-red-800" : "text-slate-600"}>
                        {fmtDateTime(d.next_review_date)}
                        {d.review_overdue && <AlertTriangle size={12} className="ml-1 inline" />}
                      </span>
                    ) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {rows.length === 0 && (
          <p className="px-3 py-10 text-center text-sm text-slate-500" data-testid="documents-empty">
            <FileText size={18} className="mx-auto mb-2 text-slate-400" />
            No documents match these filters yet.
          </p>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="new-document-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpenCheck size={17} className="text-[#134e4a]" /> New controlled document
            </DialogTitle>
            <DialogDescription>
              A permanent control number is assigned on creation. Upload Rev 1 next, then send it through technical review and medical director approval.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={create} className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-600">Title</Label>
              <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                     data-testid="doc-title-input" required minLength={3}
                     placeholder="e.g. Routine H&amp;E staining procedure" className="mt-1 h-11 border-slate-300" />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Type</Label>
                <select value={form.doc_type} onChange={(e) => setForm((f) => ({ ...f, doc_type: e.target.value }))}
                        data-testid="doc-type-input" className="mt-1 h-11 w-full border border-slate-300 bg-white px-3 text-sm">
                  {(meta?.doc_types || []).map((t) => <option key={t.code} value={t.code}>{`${t.label} (${t.code})`}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Department</Label>
                <select value={form.department} onChange={(e) => setForm((f) => ({ ...f, department: e.target.value }))}
                        data-testid="doc-department-input" className="mt-1 h-11 w-full border border-slate-300 bg-white px-3 text-sm">
                  {(meta?.departments || []).map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-600">Purpose / scope</Label>
              <Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                        data-testid="doc-description-input" className="mt-1 min-h-[80px] border-slate-300" />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Keywords (comma separated)</Label>
                <Input value={form.keywords} onChange={(e) => setForm((f) => ({ ...f, keywords: e.target.value }))}
                       data-testid="doc-keywords-input" placeholder="staining, H&amp;E, histology" className="mt-1 h-11 border-slate-300" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Review interval (months)</Label>
                <Input type="number" min="1" max="60" value={form.review_interval_months}
                       onChange={(e) => setForm((f) => ({ ...f, review_interval_months: e.target.value }))}
                       data-testid="doc-interval-input" className="mt-1 h-11 border-slate-300" />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={form.requires_acknowledgement}
                     onChange={(e) => setForm((f) => ({ ...f, requires_acknowledgement: e.target.checked }))}
                     data-testid="doc-ack-required-input" className="h-4 w-4 accent-[#134e4a]" />
              Staff must sign a read-and-understood acknowledgement
            </label>
            <Button type="submit" disabled={busy} data-testid="create-document-submit"
                    className="h-11 w-full bg-[#134e4a] hover:bg-[#0f3f3c]">
              {busy ? "Creating…" : "Create & assign control number"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
